import * as React from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid as MuiGrid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import {toast} from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {getActiveBooks} from '../../../API/books';
import {NOT_DEFINED, statusOptions} from '../../Utils/constants';

export default function BookOrderFormModal({
                                               open,
                                               onClose,
                                               initialValues = {},
                                               onSave,
                                               isSaving = false,
                                               viewOnly,
                                           }) {
    const isEdit = !!(initialValues && initialValues.order_id);
    const isPending = !!(initialValues && initialValues.order_id && initialValues.status === statusOptions.pending_review);

    const [form, setForm] = React.useState({
        order_id: '',
        order_number: '',
        purchase_id: '',
        person_name: '',
        products: [],
        order_status: statusOptions.pending_review,
        creation_date: '',
        recipient_name: '',
        delivery_address: {
            street: '',
            city: '',
            postal_code: '',
            country: ''
        },
        vendors: '',
        recipient_contact: {
            phone_number: '',
            email_address: ''
        },
        books: [],
        notes: ''
    });

    const [errors, setErrors] = React.useState({
        recipient_name: '',
        delivery_address_street: '',
        delivery_address_city: '',
        delivery_address_postal_code: '',
        delivery_address_country: '',
        recipient_contact_phone_number: '',
        recipient_contact_email_address: ''
    });

    const [availableBooks, setAvailableBooks] = React.useState([]);
    const [newBookId, setNewBookId] = React.useState('');
    const [loadingBooks, setLoadingBooks] = React.useState(false);

    // Fetch books when the modal opens
    React.useEffect(() => {
        if (open) {
            setLoadingBooks(true);
            getActiveBooks({})
                .then((response) => {
                    setAvailableBooks(response.data || []);
                })
                .catch((_error) => {
                    toast.error('Failed to load books');
                })
                .finally(() => {
                    setLoadingBooks(false);
                });
        }
    }, [open]);

    // Initialize form with initialValues
    React.useEffect(() => {
        if (!open) {
            setErrors({
                recipient_name: '',
                delivery_address_street: '',
                delivery_address_city: '',
                delivery_address_postal_code: '',
                delivery_address_country: '',
                recipient_contact_phone_number: '',
                recipient_contact_email_address: ''
            });
            setNewBookId('');
            return;
        }
        const row = initialValues || {};
        setForm({
            order_id: row.order_id || '',
            order_number: row.order_number || '',
            purchase_id: row.purchase_id || '',
            person_name: row.person_name || '',
            product: row.product.product_name,
            order_status: row.status || 'pending_review',
            creation_date: row.creation_date || '',
            recipient_name: row.recipient_name || row.person_name || '',
            delivery_address: {
                street: row.delivery_address || '',
                city: row.delivery_city || '',
                postal_code: row.delivery_postal_code || '',
                country: row.delivery_country || ''
            },
            vendors: row.vendor_name || '',
            recipient_contact: {
                phone_number: row?.contact_phone || '',
                email_address: row?.contact_email || ''
            },
            books: row.books || (row.product ? [{
                book_id: row.product.product_id || '',
                title: row.product.name || NOT_DEFINED,
                author: '',
                isbn: '',
                quantity: 1
            }] : []),
            notes: row.internal_notes || ''
        });
    }, [open, initialValues]);

    const validateForm = () => {
        const newErrors = {
            recipient_name: '',
            delivery_address_street: '',
            delivery_address_city: '',
            delivery_address_postal_code: '',
            delivery_address_country: '',
            recipient_contact_phone_number: '',
            recipient_contact_email_address: ''
        };
        let isValid = true;

        if (!form.recipient_name.trim()) {
            newErrors.recipient_name = 'Recipient Name is required';
            isValid = false;
        }

        if (!form.delivery_address.street.trim()) {
            newErrors.delivery_address_street = 'Street is required';
            isValid = false;
        }

        if (!form.delivery_address.city.trim()) {
            newErrors.delivery_address_city = 'City is required';
            isValid = false;
        }

        if (!form.delivery_address.postal_code.trim()) {
            newErrors.delivery_address_postal_code = 'Postal Code is required';
            isValid = false;
        }

        if (!form.delivery_address.country.trim()) {
            newErrors.delivery_address_country = 'Country is required';
            isValid = false;
        }

        if (form.recipient_contact.phone_number.trim() && !/^\+?[1-9]\d{1,14}$/.test(form.recipient_contact.phone_number)) {
            newErrors.recipient_contact_phone_number = 'Invalid phone number format';
            isValid = false;
        }

        if (form.recipient_contact.email_address.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.recipient_contact.email_address)) {
            newErrors.recipient_contact_email_address = 'Invalid email address format';
            isValid = false;
        }

        if (form.books.length === 0) {
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    React.useEffect(() => {
        if (open) {
            validateForm();
        }
    }, [form, open]);

    const handleChange = (field, nestedField) => (e) => {
        if (nestedField) {
            setForm((prev) => ({
                ...prev,
                [field]: {
                    ...prev[field],
                    [nestedField]: e.target.value
                }
            }));
        } else {
            setForm((prev) => ({...prev, [field]: e.target.value}));
        }
    };

    const handleBookQuantityChange = (index) => (e) => {
        const value = parseInt(e.target.value) || 1;
        if (value < 1) return;
        setForm((prev) => ({
            ...prev,
            books: prev.books.map((book, i) =>
                i === index ? {...book, quantity: value} : book
            )
        }));
    };

    const handleAddBook = () => {
        if (!newBookId) return;
        const selectedBook = availableBooks.find((book) => book.book_id === newBookId);
        if (!selectedBook) return;

        // Check for duplicate book
        if (form.books.some((book) => book.book_id === newBookId)) {
            toast.error('This book is already added to the order');
            return;
        }

        setForm((prev) => ({
            ...prev,
            books: [
                ...prev.books,
                {...selectedBook, quantity: 1}
            ]
        }));
        setNewBookId('');
    };

    const handleRemoveBook = (bookId) => () => {
        setForm((prev) => ({
            ...prev,
            books: prev.books.filter((book) => book.book_id !== bookId)
        }));
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            if (form.books.length === 0) {
                toast.error('At least one book must be added to the order');
            }
            return;
        }

        const payload = {
            order_id: form.order_id,
            order_number: form.order_number,
            recipient_name: form.recipient_name.trim(),
            delivery_address: {
                street: form.delivery_address.street.trim(),
                city: form.delivery_address.city.trim(),
                postal_code: form.delivery_address.postal_code.trim(),
                country: form.delivery_address.country.trim()
            },
            vendors: form.vendors.trim(),
            recipient_contact: {
                phone_number: form.recipient_contact.phone_number.trim(),
                email_address: form.recipient_contact.email_address.trim()
            },
            books: form.books,
            status: form.order_status,
            notes: form.notes.trim(),
        };
        onSave?.(form.order_id, payload);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Order' : 'Create Order'}</DialogTitle>
            <DialogContent dividers>
                <MuiGrid container spacing={2}>
                    {/* Non-Editable Fields */}
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Order ID"
                            disabled={viewOnly}
                            value={form.order_id}
                            fullWidth
                            InputProps={{readOnly: true}}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Purchase ID"
                            disabled={viewOnly}
                            value={form.purchase_id}
                            fullWidth
                            InputProps={{readOnly: true}}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Order Number"
                            disabled={viewOnly}
                            value={form.order_number}
                            onChange={handleChange('order_number')}
                            fullWidth
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Product(s) Assigned"
                            value={form.product}
                            disabled={viewOnly}
                            fullWidth
                            InputProps={{readOnly: true}}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Order Status</InputLabel>
                            <Select
                                value={form.order_status}
                                disabled={viewOnly}
                                onChange={handleChange('order_status')}
                                label="Order Status"
                            >
                                {Object.entries(statusOptions).map(([status, value]) => (
                                    <MenuItem key={status} value={value}>
                                        {status.replace('_', ' ').toUpperCase()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </MuiGrid>

                    {/* Editable Fields */}
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Recipient Name"
                            value={form.recipient_name}
                            onChange={handleChange('recipient_name')}
                            fullWidth
                            required
                            error={!!errors.recipient_name}
                            helperText={errors.recipient_name}
                            disabled={viewOnly}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Street"
                            value={form.delivery_address.street}
                            onChange={handleChange('delivery_address', 'street')}
                            fullWidth
                            required
                            error={!!errors.delivery_address_street}
                            helperText={errors.delivery_address_street}
                            disabled={viewOnly}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="City"
                            value={form.delivery_address.city}
                            onChange={handleChange('delivery_address', 'city')}
                            fullWidth
                            required
                            error={!!errors.delivery_address_city}
                            helperText={errors.delivery_address_city}
                            disabled={viewOnly}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Postal Code"
                            value={form.delivery_address.postal_code}
                            onChange={handleChange('delivery_address', 'postal_code')}
                            fullWidth
                            required
                            error={!!errors.delivery_address_postal_code}
                            helperText={errors.delivery_address_postal_code}
                            disabled={viewOnly}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Country</InputLabel>
                            <Select
                                value={form.delivery_address.country}
                                onChange={handleChange('delivery_address', 'country')}
                                label="Country"
                                disabled={viewOnly}
                            >
                                <MenuItem key="Denmark" value="Denmark">
                                    Denmark
                                </MenuItem>
                                <MenuItem key="Greenland" value="Greenland">
                                    Greenland
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Vendors"
                            value={form.vendors}
                            onChange={handleChange('vendors')}
                            fullWidth
                            disabled={viewOnly}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Phone Number"
                            value={form.recipient_contact.phone_number}
                            onChange={handleChange('recipient_contact', 'phone_number')}
                            fullWidth
                            error={!!errors.recipient_contact_phone_number}
                            helperText={errors.recipient_contact_phone_number}
                            disabled={viewOnly}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12} sm={6}>
                        <TextField
                            label="Email Address"
                            value={form.recipient_contact.email_address}
                            onChange={handleChange('recipient_contact', 'email_address')}
                            fullWidth
                            error={!!errors.recipient_contact_email_address}
                            helperText={errors.recipient_contact_email_address}
                            disabled={viewOnly}
                        />
                    </MuiGrid>
                    <MuiGrid item xs={12}>
                        <TextField
                            label="Internal Notes/Comments"
                            value={form.notes}
                            onChange={handleChange('notes')}
                            fullWidth
                            multiline
                            minRows={3}
                            disabled={viewOnly}
                        />
                    </MuiGrid>

                    {/* Book List & Quantities */}
                    <MuiGrid item xs={12}>
                        <Typography variant="h6">Book List & Quantities</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Book Title</TableCell>
                                    <TableCell>Author</TableCell>
                                    <TableCell>ISBN</TableCell>
                                    <TableCell>Quantity</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {form.books.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5}>No books assigned</TableCell>
                                    </TableRow>
                                ) : (
                                    form.books.map((book, index) => (
                                        <TableRow key={book.book_id}>
                                            <TableCell>{book.title}</TableCell>
                                            <TableCell>{book.author || NOT_DEFINED}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    disabled={!isPending || viewOnly}
                                                    value={book.isbn || NOT_DEFINED}
                                                    size="small"
                                                    InputProps={{readOnly: true}}
                                                    style={{width: 120}}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    type="number"
                                                    disabled={!isPending || viewOnly}
                                                    value={book.quantity}
                                                    onChange={handleBookQuantityChange(index)}
                                                    size="small"
                                                    inputProps={{min: 1}}
                                                    style={{width: 80}}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    disabled={!isPending || viewOnly}
                                                    onClick={handleRemoveBook(book.book_id)} size="small">
                                                    <DeleteIcon/>
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        <Box sx={{mt: 2, display: 'flex', alignItems: 'center', gap: 2}}>
                            <FormControl sx={{minWidth: 200}} disabled={loadingBooks}>
                                <InputLabel>Add Book</InputLabel>
                                <Select
                                    value={newBookId}
                                    disabled={!isPending || viewOnly}
                                    onChange={(e) => setNewBookId(e.target.value)}
                                    label="Add Book"
                                >
                                    <MenuItem value="">Select a book</MenuItem>
                                    {availableBooks.map((book) => (
                                        <MenuItem key={book.book_id} value={book.book_id}>
                                            {book.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <IconButton onClick={handleAddBook} disabled={!newBookId || loadingBooks}>
                                <AddIcon/>
                            </IconButton>
                            {loadingBooks && <Typography>Loading books...</Typography>}
                        </Box>
                    </MuiGrid>
                </MuiGrid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                {
                    !viewOnly && (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSaving || !!Object.values(errors).some(error => error) || form.books.length === 0}
                        >
                            {isSaving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
                        </Button>
                    )
                }
            </DialogActions>
        </Dialog>
    );
}